import { nutritionPerSchema } from '../units.js';
import { z } from 'zod';
import { foodSourceSchema } from '../nutrients.js';
import { playCategorySchema, playQualitySchema } from './play.js';
/**
 * The admin backend (M-13, M-14).
 *
 * The food library is shared: 143,000 rows belonging to no household, plus
 * whatever each household has added of its own. An admin sees across all of
 * it, which is the one place in Refuelled that crosses the tenant boundary,
 * and every write it makes is audited because every household feels it.
 */
/** One row of the search results: what you need to judge a food at a glance. */
export const adminFoodSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    brand: z.string().nullable(),
    source: foodSourceSchema,
    sourceRef: z.string().nullable(),
    category: z.string().nullable(),
    /** Per 100 g, or per 100 ml for a liquid. */
    energyKcal: z.number().nullable(),
    isLiquid: z.boolean(),
    /** Null when the food belongs to every household; otherwise whose it is. */
    tenantId: z.string().uuid().nullable(),
    tenantName: z.string().nullable(),
    quality: z.enum(['ok', 'suspect']),
    /** Null until somebody confirmed the figures (ADR-018). */
    checkedAt: z.string().nullable(),
    deactivatedAt: z.string().nullable(),
    deactivatedReason: z.string().nullable(),
    updatedAt: z.string(),
});
export const adminFoodStatusSchema = z.enum(['all', 'active', 'deactivated']);
export const adminFoodQuerySchema = z.object({
    q: z.string().max(120).optional(),
    status: adminFoodStatusSchema.default('all'),
    source: foodSourceSchema.optional(),
    /** Everything, only the shared library, or only what households added. */
    owner: z.enum(['all', 'shared', 'household']).default('all'),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
export const adminFoodListSchema = z.object({
    items: z.array(adminFoodSchema),
    /** Every match, not just this page, so the list can say how many there are. */
    total: z.number().int(),
});
/** The whole record, for the edit form. */
export const adminFoodDetailSchema = adminFoodSchema.extend({
    searchTerms: z.array(z.string()),
    densityGPerMl: z.number().nullable(),
    typicalUnitWeightG: z.number().nullable(),
    unitName: z.string().nullable(),
    /** Per 100 g/ml, or per one item (ADR-028). */
    nutritionPer: nutritionPerSchema,
    servingSizeG: z.number().nullable(),
    packSizeG: z.number().nullable(),
    packUnits: z.number().int().nullable(),
    ingredientsText: z.string().nullable(),
    attribution: z.string().nullable(),
    proteinG: z.number().nullable(),
    fatG: z.number().nullable(),
    satFatG: z.number().nullable(),
    carbG: z.number().nullable(),
    sugarG: z.number().nullable(),
    fibreG: z.number().nullable(),
    saltG: z.number().nullable(),
    /** Which households have a recipe using it, so an edit is not a surprise. */
    usedByRecipes: z.number().int(),
    /** The packet photographs a household took, for checking the figures against (ADR-041). */
    photos: z.array(z.enum(['front', 'panel', 'ingredients'])).default([]),
});
const nutrient = z.number().min(0).max(10000).nullish();
/**
 * A correction. Only the fields an admin should be trusted with: the
 * identity of the food and its per-100 g figures. Nothing here lets a
 * nutrient be computed from anything but stored reference data (rule 2);
 * this *is* the stored reference data, and correcting it is the point.
 */
export const adminFoodUpdateSchema = z.object({
    name: z.string().min(1).max(200),
    brand: z.string().max(120).nullish(),
    category: z.string().max(120).nullish(),
    searchTerms: z.array(z.string().min(1).max(60)).max(30).optional(),
    isLiquid: z.boolean(),
    densityGPerMl: z.number().min(0.1).max(3).nullish(),
    typicalUnitWeightG: z.number().min(0.1).max(10000).nullish(),
    /** What one of them is called, so the app can offer a crumpet, not 105 g. */
    unitName: z.string().min(1).max(30).nullish(),
    /** Per one item only when no weight is known for one (ADR-028). */
    nutritionPer: nutritionPerSchema.default('weight'),
    servingSizeG: z.number().min(0.1).max(10000).nullish(),
    packSizeG: z.number().min(0.1).max(100000).nullish(),
    packUnits: z.number().int().min(1).max(500).nullish(),
    energyKcal: nutrient,
    proteinG: nutrient,
    fatG: nutrient,
    satFatG: nutrient,
    carbG: nutrient,
    sugarG: nutrient,
    fibreG: nutrient,
    saltG: nutrient,
    ingredientsText: z.string().max(4000).nullish(),
    /** Marking it checked is a claim that a person looked (ADR-018). */
    checked: z.boolean().optional(),
});
/** Activate or deactivate the foods that were ticked on the search page. */
export const adminFoodBulkSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(500),
    action: z.enum(['activate', 'deactivate']),
    reason: z.string().max(300).nullish(),
});
export const adminFoodBulkResultSchema = z.object({
    changed: z.number().int(),
    /** Already in the state asked for, so nothing was written for them. */
    unchanged: z.number().int(),
});
// ------------------------------------------------- the play catalogue --
/**
 * Play options are shared reference data, edited here and deactivated
 * rather than deleted, exactly as foods are (ADR-022). Keeping the
 * catalogue good is an editorial job, which is why it lives in the admin.
 */
export const adminPlayOptionSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    emoji: z.string().nullable(),
    category: playCategorySchema,
    qualities: z.array(playQualitySchema),
    typicalMin: z.number().int(),
    blurb: z.string().nullable(),
    position: z.number().int(),
    deactivatedAt: z.string().nullable(),
    deactivatedReason: z.string().nullable(),
    /** How many times anybody has logged it, so a cull is an informed one. */
    timesLogged: z.number().int(),
});
export const adminPlayQuerySchema = z.object({
    q: z.string().max(120).optional(),
    status: adminFoodStatusSchema.default('all'),
    category: playCategorySchema.optional(),
    quality: playQualitySchema.optional(),
});
export const adminPlayOptionInputSchema = z.object({
    name: z.string().min(1).max(120),
    emoji: z.string().max(8).nullish(),
    category: playCategorySchema,
    qualities: z.array(playQualitySchema).min(1).max(7),
    typicalMin: z.number().int().min(1).max(1440),
    blurb: z.string().max(300).nullish(),
    position: z.number().int().min(0).max(10000).optional(),
});
export const adminPlayBulkSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(500),
    action: z.enum(['activate', 'deactivate']),
    reason: z.string().max(300).nullish(),
});
/**
 * A product a household photographed, waiting for an admin to check the
 * figures read from the packet against the packet itself (ADR-041).
 */
export const adminFoodReviewItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    brand: z.string().nullable(),
    tenantName: z.string().nullable(),
    quality: z.enum(['ok', 'suspect']),
    isLiquid: z.boolean(),
    nutritionPer: nutritionPerSchema,
    energyKcal: z.number().nullable(),
    proteinG: z.number().nullable(),
    carbG: z.number().nullable(),
    sugarG: z.number().nullable(),
    fatG: z.number().nullable(),
    satFatG: z.number().nullable(),
    fibreG: z.number().nullable(),
    saltG: z.number().nullable(),
    servingSizeG: z.number().nullable(),
    packSizeG: z.number().nullable(),
    ingredientsText: z.string().nullable(),
    photos: z.array(z.enum(['front', 'panel', 'ingredients'])),
    submittedAt: z.string(),
});
//# sourceMappingURL=admin.js.map